import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, LogIn, UserPlus, AlertCircle, Eye, EyeOff, CheckCircle, Clock, X, ShieldCheck, Sun, Moon, HelpCircle, KeyRound, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/contexts/ThemeContext";
import { loginUser, registerUser, getSecurityQuestions, forgotPassword, resetPassword } from "@/lib/api";
import PlanSelectionModal from "@/components/PlanSelectionModal";

export default function Auth({ mode = "login" }) {
  const { login } = useAuth();
  const { theme, toggleTheme, setThemeDirectly } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  
  // Debug: log theme changes
  console.log('Auth page theme:', theme, 'isDark:', isDark);
  
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [showPlanModal, setShowPlanModal] = useState(mode !== "login"); // auto-open on register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Security question state (for registration)
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotQuestion, setForgotQuestion] = useState("");
  const [forgotAnswer, setForgotAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [forgotStep, setForgotStep] = useState(1); // 1: enter email, 2: answer question, 3: success
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  // Sync isLogin state with mode prop
  useEffect(() => {
    const loginMode = mode === "login";
    setIsLogin(loginMode);
    if (!loginMode) setShowPlanModal(true); // auto-open plan modal for register
  }, [mode]);

  // Fetch security questions for registration
  useEffect(() => {
    if (!isLogin) {
      getSecurityQuestions()
        .then(data => setSecurityQuestions(data.questions || []))
        .catch(err => console.error("Failed to load security questions:", err));
    }
  }, [isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPendingApproval(false);

    if (!email.trim() || !password) {
      setError("Please enter your email and password");
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Show terms modal for new signups if not yet agreed
    if (!isLogin && !agreedToTerms) {
      setShowTermsModal(true);
      return;
    }

    setLoading(true);
    try {
      if (isLogin) {
        const result = await loginUser(email, password);
        // After login, sync LOCAL theme preference to server (user's most recent choice wins)
        // Don't override local theme with server preference
        const currentLocalTheme = localStorage.getItem('appTheme') || 'dark';
        if (result.user?.theme_preference !== currentLocalTheme) {
          // User's local preference differs from server - sync local to server
          import('@/lib/api').then(({ updateThemePreference }) => {
            updateThemePreference(currentLocalTheme).catch(console.error);
          });
        }
        login(result, rememberMe);
      } else {
        // Validate security question for registration
        if (!selectedQuestion || !securityAnswer.trim()) {
          setError("Please select a security question and provide an answer for password recovery");
          setLoading(false);
          return;
        }
        const result = await registerUser(email, password, selectedQuestion, securityAnswer);
        // Check if pending approval
        if (result.pending_approval) {
          setPendingApproval(true);
          setPendingMessage(result.message);
        } else {
          login(result, rememberMe);
        }
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Authentication failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAgreeToTerms = () => {
    setAgreedToTerms(true);
    setShowTermsModal(false);
    // Auto-submit the form after agreeing
    setTimeout(() => {
      document.getElementById('auth-form')?.requestSubmit();
    }, 100);
  };

  const toggleMode = () => {
    if (isLogin) {
      navigate("/register");
      setShowPlanModal(true);
    } else {
      navigate("/login");
    }
    setError("");
    setConfirmPassword("");
    setPendingApproval(false);
    setAgreedToTerms(false);
  };

  // Forgot password handlers
  const handleForgotPasswordStart = async () => {
    setForgotError("");
    if (!forgotEmail.trim()) {
      setForgotError("Please enter your email address");
      return;
    }
    
    setForgotLoading(true);
    try {
      const result = await forgotPassword(forgotEmail);
      setForgotQuestion(result.security_question);
      setForgotStep(2);
    } catch (err) {
      setForgotError(err.response?.data?.detail || "Failed to find account");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setForgotError("");
    
    if (!forgotAnswer.trim()) {
      setForgotError("Please enter your security answer");
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setForgotError("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setForgotError("Passwords do not match");
      return;
    }
    
    setForgotLoading(true);
    try {
      await resetPassword(forgotEmail, forgotAnswer, newPassword);
      setForgotSuccess("Password reset successfully! You can now login with your new password.");
      setForgotStep(3);
    } catch (err) {
      setForgotError(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStep(1);
    setForgotEmail("");
    setForgotQuestion("");
    setForgotAnswer("");
    setNewPassword("");
    setConfirmNewPassword("");
    setForgotError("");
    setForgotSuccess("");
  };

  // Show pending approval screen
  if (pendingApproval) {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: isDark ? '#0f172a' : '#f9fafb' }}
      >
        <div className="flex-grow flex flex-col items-center justify-center px-4">
          {/* Theme toggle in top right */}
          <div className="absolute top-4 right-4">
            <button
              onClick={toggleTheme}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                isDark 
                  ? 'bg-slate-800/60 hover:bg-slate-700 text-slate-300' 
                  : 'bg-white hover:bg-gray-100 text-gray-600 shadow-sm border border-gray-200'
              }`}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
              <span className="text-sm font-medium">{isDark ? 'Light' : 'Dark'}</span>
            </button>
          </div>

          <div className="flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="Doc RVU Tracker" className={`w-10 h-10 rounded-lg ${isDark ? 'brightness-[0.85]' : ''}`} />
            <div>
              <h1 className={`text-xl font-bold ${isDark ? 'text-slate-100' : 'text-gray-900'}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
                Doc RVU Tracker
              </h1>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Track your Patient Encounters, RVUs, and Hourly Revenue</p>
            </div>
          </div>

          <div className="w-full max-w-sm">
            <div className={`backdrop-blur-sm border rounded-2xl p-6 shadow-xl text-center ${
              isDark 
                ? 'bg-slate-800/50 border-slate-700/50' 
                : 'bg-white border-gray-200'
            }`}>
              <div className={`flex items-center justify-center w-16 h-16 rounded-full border mx-auto mb-4 ${
                isDark ? 'bg-amber-500/15 border-amber-500/20' : 'bg-amber-50 border-amber-200'
              }`}>
                <Clock size={32} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
              </div>
              <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>Account Pending Approval</h2>
              <p className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{pendingMessage}</p>
              
              <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border mb-6 ${
                isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-gray-50 border-gray-200'
              }`}>
                <CheckCircle size={16} className="text-emerald-400 flex-shrink-0" />
                <p className={`text-xs text-left ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                  You'll be able to log in once your account is approved by the administrator.
                </p>
              </div>

              <Button
                onClick={() => {
                  setPendingApproval(false);
                  navigate("/login");
                  setEmail("");
                  setPassword("");
                }}
                variant="outline"
                className={isDark 
                  ? "w-full border-slate-700/50 hover:bg-slate-800 text-slate-300 hover:text-slate-100"
                  : "w-full border-gray-300 hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                }
              >
                Back to Login
              </Button>
            </div>
          </div>
        </div>
        
        {/* Footer with Copyright */}
        <footer className={`border-t backdrop-blur-lg mt-8 py-6 ${
          isDark ? 'border-slate-700/40 bg-[#0f172a]/95' : 'border-gray-200 bg-white/95'
        }`}>
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className={`flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 text-sm ${
              isDark ? 'text-slate-400' : 'text-gray-600'
            }`}>
              <div className="text-center sm:text-left">
                <p>© 2026 Doc RVU Tracker. All Rights Reserved.</p>
              </div>
              <div className="text-center sm:text-right">
                <p className={isDark ? 'text-slate-500' : 'text-gray-500'}>Built for medical professionals</p>
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>DocRVUTracker@gmail.com</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: isDark ? '#0f172a' : '#f9fafb' }}
    >
      {/* Plan selection modal — auto-opens for register mode */}
      {!isLogin && showPlanModal && (
        <PlanSelectionModal onClose={() => setShowPlanModal(false)} />
      )}
      <div className="flex-grow flex flex-col items-center justify-center px-4">
        {/* Theme toggle in top right */}
        <div className="absolute top-4 right-4">
          <button
            onClick={toggleTheme}
            data-testid="auth-theme-toggle"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              isDark 
                ? 'bg-slate-800/60 hover:bg-slate-700 text-slate-300' 
                : 'bg-white hover:bg-gray-100 text-gray-600 shadow-sm border border-gray-200'
            }`}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
            <span className="text-sm font-medium">{isDark ? 'Light' : 'Dark'}</span>
          </button>
        </div>

        {/* Logo and Title - centered with login box */}
        <div className="flex items-center gap-4 mb-10">
          <img src="/logo.png" alt="Doc RVU Tracker" className={`w-14 h-14 rounded-xl flex-shrink-0 ${isDark ? 'brightness-[0.85]' : ''}`} />
          <div>
            <h1 className={`text-3xl font-bold whitespace-nowrap ${isDark ? 'text-slate-100' : 'text-gray-900'}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
              Doc RVU Tracker
            </h1>
            <p className={`text-sm tracking-wide ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>Track your Patient Encounters, RVUs, and Hourly Revenue</p>
          </div>
        </div>

        {/* Auth Card */}
        <div className="w-full max-w-sm">
          <div className={`backdrop-blur-sm border rounded-2xl p-6 shadow-xl ${
            isDark 
              ? 'bg-slate-800/50 border-slate-700/50' 
              : 'bg-white border-gray-200'
          }`}>
            <h2 className={`text-xl font-semibold mb-1 text-center ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>
              {isLogin ? "Welcome Back" : "Create Account"}
            </h2>
            <p className={`text-sm mb-6 text-center ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              {isLogin ? "Sign in to continue" : "Register to get started"}
            </p>

            {error && (
              <div data-testid="auth-error" className={`flex items-center gap-2 mb-4 px-3 py-2.5 rounded-lg border ${
                isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'
              }`}>
                <AlertCircle size={14} className={isDark ? 'text-red-400' : 'text-red-600'} />
                <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
              </div>
            )}

            <form id="auth-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`text-xs uppercase tracking-widest font-semibold mb-1.5 block ${
                  isDark ? 'text-slate-500' : 'text-gray-500'
                }`}>
                  Email
                </label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                  <Input
                    data-testid="email-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`pl-10 h-11 ${
                      isDark 
                        ? 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                        : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className={`text-xs uppercase tracking-widest font-semibold mb-1.5 block ${
                  isDark ? 'text-slate-500' : 'text-gray-500'
                }`}>
                  Password
                </label>
                <div className="relative">
                  <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                  <Input
                    data-testid="password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`pl-10 pr-10 h-11 ${
                      isDark 
                        ? 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                        : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                      isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div>
                  <label className={`text-xs uppercase tracking-widest font-semibold mb-1.5 block ${
                    isDark ? 'text-slate-500' : 'text-gray-500'
                  }`}>
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                    <Input
                      data-testid="confirm-password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`pl-10 h-11 ${
                        isDark 
                          ? 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                          : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
                      }`}
                    />
                  </div>
                </div>
              )}

              {/* Security Question for Registration */}
              {!isLogin && (
                <div className="space-y-3">
                  <div>
                    <label className={`text-xs uppercase tracking-widest font-semibold mb-1.5 block ${
                      isDark ? 'text-slate-500' : 'text-gray-500'
                    }`}>
                      Security Question <span className={isDark ? 'text-slate-600' : 'text-gray-400'}>(for password recovery)</span>
                    </label>
                    <div className="relative">
                      <HelpCircle className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                      <select
                        data-testid="security-question-select"
                        value={selectedQuestion}
                        onChange={(e) => setSelectedQuestion(e.target.value)}
                        className={`w-full pl-10 pr-4 h-11 rounded-md border appearance-none cursor-pointer ${
                          isDark 
                            ? 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200'
                            : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-blue-500 text-gray-900'
                        }`}
                      >
                        <option value="">Select a security question...</option>
                        {securityQuestions.map((q, idx) => (
                          <option key={idx} value={q}>{q}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {selectedQuestion && (
                    <div>
                      <label className={`text-xs uppercase tracking-widest font-semibold mb-1.5 block ${
                        isDark ? 'text-slate-500' : 'text-gray-500'
                      }`}>
                        Your Answer
                      </label>
                      <div className="relative">
                        <KeyRound className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                        <Input
                          data-testid="security-answer-input"
                          type="text"
                          placeholder="Enter your answer..."
                          value={securityAnswer}
                          onChange={(e) => setSecurityAnswer(e.target.value)}
                          className={`pl-10 h-11 ${
                            isDark 
                              ? 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                              : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
                          }`}
                        />
                      </div>
                      <p className={`text-xs mt-1 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
                        Remember this answer - you'll need it to reset your password
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember-me"
                    data-testid="remember-me-checkbox"
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    className={isDark 
                      ? "border-slate-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                      : "border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    }
                  />
                  <label htmlFor="remember-me" className={`text-sm cursor-pointer ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                    Remember me
                  </label>
                </div>
                
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className={`text-sm font-medium hover:underline ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                  >
                    Forgot Password?
                  </button>
                )}
              </div>

              <Button
                type="submit"
                data-testid="auth-submit-btn"
                disabled={loading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-[0_4px_0_0_rgba(0,0,0,0.3)] active:shadow-none active:translate-y-[4px] transition-all"
              >
                {loading ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : isLogin ? (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Sign In
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Create Account
                  </>
                )}
              </Button>
            </form>

            <div className={`mt-6 pt-4 border-t text-center ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
              <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                {isLogin ? "Don't have an account?" : "Already have an account?"}
                <button
                  data-testid="auth-toggle-btn"
                  onClick={toggleMode}
                  className="ml-1.5 text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                >
                  {isLogin ? "Sign up" : "Sign in"}
                </button>
              </p>
              <p className={`text-xs mt-2 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
                Register an account to choose your subscription or<br />start free trial
              </p>
            </div>
          </div>

          <p className={`text-xs text-center mt-4 ${isDark ? 'text-slate-600' : 'text-gray-500'}`}>
            Your data is private and secure
          </p>
        </div>

        {/* Terms Agreement Modal */}
        {showTermsModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className={`border rounded-2xl shadow-2xl max-w-md w-full overflow-hidden ${
              isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
            }`}>
              {/* Modal Header */}
              <div className={`flex items-center justify-between px-6 py-4 border-b ${
                isDark ? 'border-slate-700' : 'border-gray-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    isDark ? 'bg-emerald-500/15' : 'bg-emerald-50'
                  }`}>
                    <ShieldCheck size={20} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
                  </div>
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>Terms of Use</h3>
                </div>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className={`transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-5">
                <p className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  By creating an account, you agree to the following terms:
                </p>
                <div className={`border rounded-lg p-4 mb-4 ${
                  isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-gray-50 border-gray-200'
                }`}>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                    You may <span className="text-red-500 font-semibold">NOT</span> copy, reproduce, repurpose, resell, or redistribute any content present on this program and website. All content, features, and functionality are the exclusive property of Doc RVU Tracker and are protected by copyright and other intellectual property laws.
                  </p>
                </div>
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                  Violation of these terms may result in account termination and legal action.
                </p>
              </div>

              {/* Modal Footer */}
              <div className={`px-6 py-4 border-t flex gap-3 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <Button
                  onClick={() => setShowTermsModal(false)}
                  variant="outline"
                  className={`flex-1 ${
                    isDark 
                      ? 'border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAgreeToTerms}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                >
                  I Agree
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className={`border rounded-2xl shadow-2xl max-w-md w-full overflow-hidden ${
              isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
            }`}>
              {/* Modal Header */}
              <div className={`flex items-center justify-between px-6 py-4 border-b ${
                isDark ? 'border-slate-700' : 'border-gray-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    isDark ? 'bg-blue-500/15' : 'bg-blue-50'
                  }`}>
                    <KeyRound size={20} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                  </div>
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>
                    {forgotStep === 1 && "Reset Password"}
                    {forgotStep === 2 && "Security Question"}
                    {forgotStep === 3 && "Password Reset"}
                  </h3>
                </div>
                <button
                  onClick={closeForgotPassword}
                  className={`transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-5">
                {/* Error Message */}
                {forgotError && (
                  <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                    isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-600'
                  }`}>
                    <AlertCircle size={16} />
                    <span className="text-sm">{forgotError}</span>
                  </div>
                )}

                {/* Step 1: Enter Email */}
                {forgotStep === 1 && (
                  <div className="space-y-4">
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                      Enter your email address and we'll show you your security question.
                    </p>
                    <div>
                      <label className={`text-xs uppercase tracking-widest font-semibold mb-1.5 block ${
                        isDark ? 'text-slate-500' : 'text-gray-500'
                      }`}>
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className={`pl-10 h-11 ${
                            isDark 
                              ? 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                              : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
                          }`}
                          onKeyDown={(e) => e.key === 'Enter' && handleForgotPasswordStart()}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Answer Security Question */}
                {forgotStep === 2 && (
                  <div className="space-y-4">
                    <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-900/50 border border-slate-700/50' : 'bg-gray-50 border border-gray-200'}`}>
                      <p className={`text-xs uppercase tracking-widest font-semibold mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                        Security Question
                      </p>
                      <p className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>
                        {forgotQuestion}
                      </p>
                    </div>
                    
                    <div>
                      <label className={`text-xs uppercase tracking-widest font-semibold mb-1.5 block ${
                        isDark ? 'text-slate-500' : 'text-gray-500'
                      }`}>
                        Your Answer
                      </label>
                      <Input
                        type="text"
                        placeholder="Enter your answer..."
                        value={forgotAnswer}
                        onChange={(e) => setForgotAnswer(e.target.value)}
                        className={`h-11 ${
                          isDark 
                            ? 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                            : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
                        }`}
                      />
                    </div>

                    <div>
                      <label className={`text-xs uppercase tracking-widest font-semibold mb-1.5 block ${
                        isDark ? 'text-slate-500' : 'text-gray-500'
                      }`}>
                        New Password
                      </label>
                      <Input
                        type="password"
                        placeholder="Enter new password..."
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className={`h-11 ${
                          isDark 
                            ? 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                            : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
                        }`}
                      />
                    </div>

                    <div>
                      <label className={`text-xs uppercase tracking-widest font-semibold mb-1.5 block ${
                        isDark ? 'text-slate-500' : 'text-gray-500'
                      }`}>
                        Confirm New Password
                      </label>
                      <Input
                        type="password"
                        placeholder="Confirm new password..."
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className={`h-11 ${
                          isDark 
                            ? 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                            : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
                        }`}
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: Success */}
                {forgotStep === 3 && (
                  <div className="text-center py-4">
                    <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                      isDark ? 'bg-green-500/20' : 'bg-green-100'
                    }`}>
                      <CheckCircle className={`h-8 w-8 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                    </div>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                      {forgotSuccess}
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className={`px-6 py-4 border-t flex gap-3 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                {forgotStep === 1 && (
                  <>
                    <Button
                      onClick={closeForgotPassword}
                      variant="outline"
                      className={`flex-1 ${
                        isDark 
                          ? 'border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleForgotPasswordStart}
                      disabled={forgotLoading}
                      className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-semibold"
                    >
                      {forgotLoading ? (
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        "Continue"
                      )}
                    </Button>
                  </>
                )}

                {forgotStep === 2 && (
                  <>
                    <Button
                      onClick={() => { setForgotStep(1); setForgotError(""); }}
                      variant="outline"
                      className={`flex-1 ${
                        isDark 
                          ? 'border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back
                    </Button>
                    <Button
                      onClick={handleResetPassword}
                      disabled={forgotLoading}
                      className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-semibold"
                    >
                      {forgotLoading ? (
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        "Reset Password"
                      )}
                    </Button>
                  </>
                )}

                {forgotStep === 3 && (
                  <Button
                    onClick={closeForgotPassword}
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold"
                  >
                    Back to Login
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer with Copyright */}
      <footer className={`border-t backdrop-blur-lg mt-8 py-6 ${
        isDark ? 'border-slate-700/40 bg-[#0f172a]/95' : 'border-gray-200 bg-white/95'
      }`}>
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className={`flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 text-sm ${
            isDark ? 'text-slate-400' : 'text-gray-600'
          }`}>
            <div className="text-center sm:text-left space-y-0.5">
              <p>© 2026 Doc RVU Tracker. All Rights Reserved.</p>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>DocRVUTracker@gmail.com</p>
            </div>
            <p className={isDark ? 'text-slate-500' : 'text-gray-500'}>Built for medical professionals</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
